import React, {
  useEffect, useRef, useState,
} from 'react';
import useWindowSize from '../../hooks/useWindowSize';

import './HorizontalScroller.scss';

// eslint-disable-next-line no-shadow
enum ScrollDirection {
  LEFT = -1,
  RIGHT = 1,
}

interface Props {
  children: React.ReactNode;
}

const HorizontalScroller = ({ children }: Props): React.ReactElement => {
  // variables and state
  const [containerWidth, setContainerWitdh] = useState(0);
  const [contentWidth, setContentWitdh] = useState(0);
  const [showNav, setShowNav] = useState(false);
  const [isScrolledRight, setIsScrolledRight] = useState(false);
  const [requireScroll, setRequireScroll] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const windowSize = useWindowSize();

  const containerElement = useRef<HTMLDivElement>(null);
  const contentElement = useRef<HTMLDivElement>(null);

  // set the container width. reset on window resize
  useEffect(() => {
    const { current } = containerElement;

    if (current) {
      const { scrollWidth } = current;
      setContainerWitdh(scrollWidth);
    }
  }, [containerElement.current, windowSize.width]);

  // set the content width. reset on window resize
  useEffect(() => {
    const { current } = contentElement;

    if (current) {
      const { scrollWidth } = current;
      setContentWitdh(scrollWidth);
    }
  }, [contentElement.current, windowSize.width]);

  // set require scroll whenever the container or content width changes
  useEffect(() => {
    setRequireScroll(contentWidth > containerWidth);
  }, [contentWidth, containerWidth]);

  // set show nav every time the require scroll property updates
  useEffect(() => {
    setShowNav(!windowSize.isTouchDevice && requireScroll);
  }, [requireScroll, windowSize.isTouchDevice]);

  // handle a scroll event
  const handleInnerScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollLeft, offsetWidth, scrollWidth } = event.currentTarget;

    setIsScrolledRight(scrollLeft > 0);
    // something, ie the minus 5, needs to happen... 0 is not ok
    setCanScrollRight((scrollLeft + offsetWidth) < scrollWidth - 5);
  };

  // simplistically handle left and right clicks on the nav items,
  // scroll the content element 95% on click
  const handleNavClick = ((direction: ScrollDirection) => {
    const { current } = contentElement;

    if (current) {
      const moveBy = (current.clientWidth * 0.95) * direction;
      const scrollLeft = current.scrollLeft + moveBy;

      current.scrollLeft = scrollLeft;
    }
  });

  // ducks are now in a row, set vars used for render state and css classes
  const disableScrollLeft = !isScrolledRight;
  const disableScrollRight = !canScrollRight;

  const navClassNames = ['button', 'icon-button', 'transparent'];
  const navLeftClassNames = [
    ...navClassNames,
    'scroll-left',
    disableScrollLeft ? 'disabled' : undefined,
  ];
  const navRightClassNames = [
    ...navClassNames,
    'scroll-right',
    disableScrollRight ? 'disabled' : undefined,
  ];
  const horizontalScrollerWrapperClassNames = [
    'horizontal-scroller-wrapper',
    requireScroll ? 'on' : undefined,
  ];

  return (
    <div className="horizontal-scroller" ref={containerElement}>
      {showNav && (
        <button
          type="button"
          className={navLeftClassNames.join(' ')}
          onClick={() => handleNavClick(ScrollDirection.LEFT)}
        >
          <span>
            &lt;
          </span>
        </button>
      )}

      <div className={horizontalScrollerWrapperClassNames.join(' ')}>
        {requireScroll && (
          <div className="horizontal-scroller-signifier" />
        )}

        <div
          className="horizontal-scroller-inner"
          ref={contentElement}
          onScroll={handleInnerScroll}
        >
          { children }
        </div>

        {requireScroll && (
          <div className="horizontal-scroller-signifier" />
        )}
      </div>

      {showNav && (
        <button
          type="button"
          className={navRightClassNames.join(' ')}
          onClick={() => handleNavClick(ScrollDirection.RIGHT)}
        >
          <span>
            &gt;
          </span>
        </button>
      )}
    </div>
  );
};

export default HorizontalScroller;
